import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { FinalStandingsTableColumns } from "~pages/FinalStandings/enums/finalStandingsTableColumns.ts";
import { useFetchAllFinalPlacings } from "~pages/FinalStandings/hooks/useFetchAllFinalPlacings";
import { useFinalStandingsService } from "~pages/FinalStandings/hooks/useFinalStandingsService.ts";
import { useGetFinalStandingsTableColumns } from "~pages/FinalStandings/hooks/useGetFinalStandingsTableColumns.tsx";
import type { PlacingTableRow } from "~pages/FinalStandings/types/placingTableRow";
import type { SearchFilterParams } from "~pages/FinalStandings/types/searchFilterParams";

const FINAL_STANDINGS_PARAMS: SearchFilterParams = {
  limit: "1000",
  offset: "0",
  include: ["persons", "entities"].join(","),
};

const DEFAULT_SORTER = { columnKey: FinalStandingsTableColumns.ResultPlace, order: "ascend" as const };

const FinalStandingsPage = () => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { sport, organizationId, seasonId } = useParams();
  const { competitorType, uiConfiguration } = useSportConfiguration();
  const [massEditMode, setMassEditMode] = useState(false);
  const paginationConfig = useHandlePagination(20);
  const { data, isLoading, refetch } = useFetchAllFinalPlacings(FINAL_STANDINGS_PARAMS);
  const { deletePlacing, isDeletePending: isDeletePlacingPending } = useFinalStandingsService();
  const columns = useGetFinalStandingsTableColumns(massEditMode);
  const dataTableUiConfiguration = uiConfiguration.pages.finalStandings.listPage;
  const rows = (data?.data || []) as PlacingTableRow[];

  const placingPermission =
    competitorType === CompetitorTypes.Entity ? PermissionNames.SEASON_ENTITY_PLACING : PermissionNames.SEASON_PERSON_PLACING;

  const rowKey = competitorType === CompetitorTypes.Person ? "personId" : "entityId";

  const onMassEditModeChangedHandler = useCallback(
    (checked: boolean) => {
      setMassEditMode(checked);
      if (!checked) {
        void queryClient.invalidateQueries({
          queryKey: ["final-standings", sport, organizationId, seasonId],
        });
      }
    },
    [organizationId, queryClient, seasonId, sport],
  );

  return (
    <DataTable<PlacingTableRow>
      title={formatMessage({ id: "finalStandings" })}
      columns={columns}
      rowKey={rowKey}
      dataSource={rows}
      isDataLoading={isLoading || isDeletePlacingPending}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.finalStandings`}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.finalStandings`,
        defaultSorter: DEFAULT_SORTER,
      }}
      permissionNames={[placingPermission]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      onMassEditModeChangedHandler={onMassEditModeChangedHandler}
      massEditMode={massEditMode}
      handleRefresh={async () => {
        await refetch();
      }}
      paginationConfig={{ ...paginationConfig, total: rows.length }}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => {
              if (row.placingId) {
                void deletePlacing(row.placingId);
              }
            },
            hidden: (row) => !row.placingId,
            itemName: () => formatMessage({ id: "standings.final" }),
            loading: isDeletePlacingPending,
          },
        },
      }}
    />
  );
};

export default FinalStandingsPage;
